import React, { useEffect, useMemo, useState } from 'react';
import './BillingIntake.css';

const EMPTY_FORM = {
  legalBusinessName: '',
  taxCode: '',
  businessRegistrationNumber: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  ward: '',
  district: '',
  cityProvince: '',
  countryCode: 'VN',
  billingContactName: '',
  billingEmail: '',
  billingPhone: '',
  signerName: '',
  signerTitle: '',
};

async function readJson(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(text || 'Request failed');
  }
}

function normalizeForm(profile = {}) {
  return {
    ...EMPTY_FORM,
    legalBusinessName: String(profile.legalBusinessName || ''),
    taxCode: String(profile.taxCode || ''),
    businessRegistrationNumber: String(profile.businessRegistrationNumber || ''),
    billingAddressLine1: String(profile.billingAddressLine1 || ''),
    billingAddressLine2: String(profile.billingAddressLine2 || ''),
    ward: String(profile.ward || ''),
    district: String(profile.district || ''),
    cityProvince: String(profile.cityProvince || ''),
    countryCode: String(profile.countryCode || 'VN').toUpperCase() || 'VN',
    billingContactName: String(profile.billingContactName || ''),
    billingEmail: String(profile.billingEmail || ''),
    billingPhone: String(profile.billingPhone || ''),
    signerName: String(profile.signerName || ''),
    signerTitle: String(profile.signerTitle || ''),
  };
}

function formatScopeLabel(scope) {
  if (scope === 'selected_sites') return 'Selected sites';
  if (scope === 'per_site') return 'Single site';
  return 'All sites';
}

export default function BillingIntake({ match }) {
  const token = String(match?.params?.token || '').trim();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [payload, setPayload] = useState(null);
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM }));

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError('');
        setSuccessMessage('');
        const response = await fetch(`/api/billing-intake/${encodeURIComponent(token)}`);
        const data = await readJson(response);
        if (!response.ok) {
          throw new Error(data.message || 'Failed to load billing intake form');
        }
        if (cancelled) return;
        setPayload(data);
        setForm(normalizeForm(data.billingProfile));
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load billing intake form');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (token) {
      load();
    } else {
      setLoading(false);
      setError('Billing intake link is invalid.');
    }

    return () => {
      cancelled = true;
    };
  }, [token]);

  const coveredSiteLabel = useMemo(() => {
    const coveredSites = Array.isArray(payload?.account?.coveredSites) ? payload.account.coveredSites : [];
    if (!coveredSites.length) return 'No sites selected yet';
    return coveredSites.map((site) => site.name).join(' | ');
  }, [payload]);

  const handleChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: field === 'countryCode' ? String(value || '').toUpperCase() : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');
      const response = await fetch(`/api/billing-intake/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });
      const data = await readJson(response);
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save billing details');
      }
      setPayload(data);
      setForm(normalizeForm(data.billingProfile));
      setSuccessMessage('Your company details have been saved. You can close this page or return to it later to update them.');
    } catch (err) {
      setError(err.message || 'Failed to save billing details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="billing-intake-page">
      <div className="billing-intake-shell">
        <div className="billing-intake-card">
          <div className="billing-intake-eyebrow">Billing Details</div>
          <h1>Company details for invoicing and contracts</h1>
          <p className="billing-intake-intro">
            Fill out the legal business details you want Soundcheck to use for invoices, agreements, and billing records.
          </p>

          {loading ? (
            <div className="billing-intake-status">Loading billing form…</div>
          ) : error ? (
            <div className="billing-intake-alert billing-intake-alert-error">{error}</div>
          ) : (
            <>
              <div className="billing-intake-summary">
                <div>
                  <div className="billing-intake-summary-label">Prepared by</div>
                  <div className="billing-intake-summary-value">{payload?.provider?.companyName || 'Soundcheck'}</div>
                </div>
                <div>
                  <div className="billing-intake-summary-label">Scope</div>
                  <div className="billing-intake-summary-value">{formatScopeLabel(payload?.account?.billingScope)}</div>
                </div>
                <div>
                  <div className="billing-intake-summary-label">Covered sites</div>
                  <div className="billing-intake-summary-value">{coveredSiteLabel}</div>
                </div>
              </div>

              {successMessage && (
                <div className="billing-intake-alert billing-intake-alert-success">{successMessage}</div>
              )}

              <form className="billing-intake-form" onSubmit={handleSubmit}>
                <div className="billing-intake-grid billing-intake-grid-two">
                  <label>
                    <span>Legal business name</span>
                    <input
                      value={form.legalBusinessName}
                      onChange={(event) => handleChange('legalBusinessName', event.target.value)}
                      placeholder="Registered company / household business name"
                      required
                    />
                  </label>
                  <label>
                    <span>Tax code</span>
                    <input
                      value={form.taxCode}
                      onChange={(event) => handleChange('taxCode', event.target.value)}
                      placeholder="Vietnam tax code"
                      required
                    />
                  </label>
                  <label>
                    <span>Business registration no.</span>
                    <input
                      value={form.businessRegistrationNumber}
                      onChange={(event) => handleChange('businessRegistrationNumber', event.target.value)}
                    />
                  </label>
                  <label>
                    <span>Country code</span>
                    <input
                      value={form.countryCode}
                      onChange={(event) => handleChange('countryCode', event.target.value)}
                      maxLength={2}
                    />
                  </label>
                </div>

                <div className="billing-intake-grid billing-intake-grid-two">
                  <label>
                    <span>Billing address line 1</span>
                    <input
                      value={form.billingAddressLine1}
                      onChange={(event) => handleChange('billingAddressLine1', event.target.value)}
                      placeholder="Street / building / number"
                      required
                    />
                  </label>
                  <label>
                    <span>Billing address line 2</span>
                    <input
                      value={form.billingAddressLine2}
                      onChange={(event) => handleChange('billingAddressLine2', event.target.value)}
                    />
                  </label>
                  <label>
                    <span>Ward</span>
                    <input
                      value={form.ward}
                      onChange={(event) => handleChange('ward', event.target.value)}
                    />
                  </label>
                  <label>
                    <span>District</span>
                    <input
                      value={form.district}
                      onChange={(event) => handleChange('district', event.target.value)}
                    />
                  </label>
                  <label>
                    <span>City / province</span>
                    <input
                      value={form.cityProvince}
                      onChange={(event) => handleChange('cityProvince', event.target.value)}
                      required
                    />
                  </label>
                </div>

                <div className="billing-intake-grid billing-intake-grid-two">
                  <label>
                    <span>Billing contact name</span>
                    <input
                      value={form.billingContactName}
                      onChange={(event) => handleChange('billingContactName', event.target.value)}
                    />
                  </label>
                  <label>
                    <span>Billing email</span>
                    <input
                      type="email"
                      value={form.billingEmail}
                      onChange={(event) => handleChange('billingEmail', event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>Billing phone</span>
                    <input
                      value={form.billingPhone}
                      onChange={(event) => handleChange('billingPhone', event.target.value)}
                    />
                  </label>
                  <label>
                    <span>Signer name</span>
                    <input
                      value={form.signerName}
                      onChange={(event) => handleChange('signerName', event.target.value)}
                      required
                    />
                  </label>
                  <label>
                    <span>Signer title</span>
                    <input
                      value={form.signerTitle}
                      onChange={(event) => handleChange('signerTitle', event.target.value)}
                      placeholder="Director / Owner / Manager"
                      required
                    />
                  </label>
                </div>

                <div className="billing-intake-actions">
                  <button type="submit" disabled={saving}>
                    {saving ? 'Saving…' : 'Save Billing Details'}
                  </button>
                  <div className="billing-intake-note">
                    These details will be used for invoices and contract PDFs generated by Soundcheck.
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
